import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getPublicReceipt } from "../lib/katlas_client";
import { CheckCircle, XCircle, Warning, ShieldCheck } from "@phosphor-icons/react";

export const PublicReceipt = () => {
  const { hash } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getPublicReceipt(hash);
        if (alive) setState({ loading: false, data, error: null });
      } catch (err) {
        if (alive)
          setState({
            loading: false,
            data: null,
            error: err?.response?.status === 404 ? "not_found" : "error",
          });
      }
    })();
    return () => {
      alive = false;
    };
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="h-14 border-b border-stone-200 bg-white flex items-center justify-between px-6 shrink-0">
        <Link to="/" className="flex items-center gap-2.5 text-stone-900 hover:opacity-80">
          <div className="w-6 h-6 rounded-sm bg-stone-900 flex items-center justify-center">
            <span className="font-serif italic text-white text-sm leading-none">Ai</span>
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">AICierge</div>
            <div className="text-sm font-medium">Public receipt</div>
          </div>
        </Link>
        <div className="text-[11px] uppercase tracking-wider text-stone-500">Verification view</div>
      </header>

      <main className="flex-1 p-6 flex items-start justify-center">
        <div className="w-full max-w-2xl">
          {state.loading && <div className="text-sm text-stone-500">Looking up receipt…</div>}

          {state.error === "not_found" && (
            <div data-testid="public-receipt-missing" className="bg-amber-50 border border-amber-200 rounded-sm p-5">
              <div className="flex items-center gap-1.5 text-amber-800 text-[11px] uppercase tracking-wider font-medium">
                <Warning size={13} weight="fill" /> Receipt not found
              </div>
              <p className="text-sm text-amber-900 mt-1.5 leading-relaxed">
                This receipt hash does not match a published record, or the record has not been
                marked public-shareable.
              </p>
              <Link to="/" className="text-xs text-stone-600 underline mt-3 inline-block">
                Back to AICierge
              </Link>
            </div>
          )}

          {state.error === "error" && (
            <div className="bg-rose-50 border border-rose-200 rounded-sm p-5">
              <div className="text-rose-800 text-sm">
                Could not verify this receipt right now. Please try again later.
              </div>
            </div>
          )}

          {state.data && <PublicReceiptCard receipt={state.data} hash={hash} />}
        </div>
      </main>

      <footer className="px-6 py-3 border-t border-stone-200 bg-white text-[11px] text-stone-500 text-center">
        This public view shows only proof fields. Token values, full envelopes and private payload
        are never exposed here.
      </footer>
    </div>
  );
};

const PublicReceiptCard = ({ receipt, hash }) => {
  const verified = (receipt.verification_status || "").toLowerCase().includes("verif");
  return (
    <div
      data-testid="public-receipt"
      className="bg-[#FFFCF8] border border-dashed border-stone-300 rounded-sm p-6 paper-texture relative overflow-hidden"
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between border-b border-stone-200 pb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
              KATLAS · Public decision record
            </div>
            <div className="font-serif italic text-2xl text-stone-900 mt-0.5 leading-tight">
              Attributable receipt
            </div>
          </div>
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] uppercase tracking-wider font-medium border ${
              verified
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-stone-100 text-stone-700 border-stone-200"
            }`}
          >
            {verified ? <CheckCircle size={12} weight="fill" /> : <ShieldCheck size={12} />}
            {receipt.verification_status || "unknown"}
          </div>
        </div>

        <dl className="font-mono text-[11px] text-stone-700 mt-3 space-y-1">
          <Row k="receipt hash" v={receipt.receipt_hash || hash} />
          <Row k="signer issuer" v={receipt.signer_issuer || "—"} />
          <Row k="signer key id" v={receipt.signer_key_id || "—"} />
          <Row k="signer fp" v={receipt.signer_fingerprint || "—"} />
          <Row k="node timestamp" v={receipt.node_timestamp || "—"} />
          <Row k="outcome" v={receipt.outcome || "—"} />
          <Row
            k="shared evidence"
            v={
              (receipt.shared_evidence_refs || []).length
                ? receipt.shared_evidence_refs.join(", ")
                : "—"
            }
          />
          <Row k="withheld count" v={String(receipt.withheld_evidence_count ?? 0)} />
        </dl>

        <div className="mt-4 pt-2 border-t border-stone-200 text-[11px] text-stone-600 leading-relaxed italic">
          {receipt.data_boundary_statement ||
            "This receipt proves the governed AI-assisted action was recorded without exposing more information than needed."}
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex gap-3">
    <span className="text-stone-400 w-32 shrink-0 uppercase tracking-wider">{k}</span>
    <span className="text-stone-800 flex-1 break-words">{v}</span>
  </div>
);
